import { BasePayloadMapper } from '@ps/utils';
import {
  completesToClick,
  SaveTemplateRequest,
  StandardSurveyPayloadInput,
  StandardSurveyPayloadMapper,
  SURVEY_CATEGORY_CODE,
} from '@ps/survey-utils';

import { SaveTemplateRequestDto } from '../dtos/save-template.dto';

export interface SaveTemplatePayloadInput {
  request: SaveTemplateRequest;
  surveyId: number;
  survey: StandardSurveyPayloadInput;
  isFeotEnabled: boolean;
  isServiceBuyer: boolean;
}

/**
 * Builds the save/update-template body from the survey currently on screen.
 *
 * Targeting is taken from `StandardSurveyPayloadMapper` rather than re-derived:
 * its qualification/quota helpers are private, order-dependent, and carry the
 * child-age-gender remap. Reusing them guarantees a template's targeting is
 * identical to what the survey itself would save.
 */
export class SaveTemplatePayloadMapper extends BasePayloadMapper<
  SaveTemplatePayloadInput,
  SaveTemplateRequestDto
> {
  private readonly _surveyMapper = new StandardSurveyPayloadMapper();

  public toPayload({
    request,
    surveyId,
    survey,
    isFeotEnabled,
    isServiceBuyer,
  }: SaveTemplatePayloadInput): SaveTemplateRequestDto {
    const surveyPayload = this._surveyMapper.toPayload(survey);
    const { metadata } = survey;

    return {
      folder: request.folderId,
      is_suppliers_list: request.saveSupplierListAndAllocation,
      survey_id: surveyId,
      // The edit page is single-country only; revisit with MC support.
      is_mc_template: false,
      template: {
        name: request.templateName,
        survey_title: surveyPayload.survey_title,
        click_balancing: surveyPayload.click_balancing,
        completes_required: surveyPayload.completes_required,
        estimated_clicks: this._estimatedClicks(metadata),
        expected_ir: surveyPayload.expected_ir,
        expected_loi: surveyPayload.expected_loi,
        field_time: surveyPayload.field_time,
        is_feot_enabled: isFeotEnabled,
        qualifications: surveyPayload.qualifications ?? [],
        quotas: surveyPayload.quotas ?? [],
        schedule_launch: { enable: false, info: {} },
        soft_launch: { enable: false },
        survey_category_code: SURVEY_CATEGORY_CODE,
        survey_localization: surveyPayload.survey_localization,
        is_service_buyer: isServiceBuyer,
      },
    };
  }

  private _estimatedClicks({
    clickBalancing,
    completes,
    incidence,
  }: StandardSurveyPayloadInput['metadata']): number {
    if (!clickBalancing || !completes || !incidence) {
      return 0;
    }
    return completesToClick(completes, incidence);
  }
}
